import { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAppStore } from '@/hooks/useAppStore';
import { SUPPORTED_INPUT_FORMATS } from '@/types';

export function UploadZone() {
  const addFiles = useAppStore((state) => state.addFiles);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
        SUPPORTED_INPUT_FORMATS.includes(file.type)
      );

      if (droppedFiles.length > 0) {
        addFiles(droppedFiles);
      }
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []).filter((file) =>
        SUPPORTED_INPUT_FORMATS.includes(file.type)
      );

      if (selectedFiles.length > 0) {
        addFiles(selectedFiles);
      }

      // Reset input
      e.target.value = '';
    },
    [addFiles]
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative"
    >
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="relative group"
      >
        <label
          htmlFor="file-upload"
          className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-surface-highlight rounded-2xl cursor-pointer transition-all duration-300 hover:border-primary hover:bg-surface-highlight/30"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4 text-center">
            <motion.div
              whileHover={{ scale: 1.1 }}
              className="w-16 h-16 mb-4 rounded-full bg-surface-highlight flex items-center justify-center"
            >
              <svg
                className="w-8 h-8 text-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </motion.div>
            
            <p className="mb-2 text-lg font-medium text-text-primary">
              <span className="gradient-text">拖拽文件到这里</span>
            </p>
            <p className="mb-2 text-sm text-text-secondary">
              或点击选择文件
            </p>
            <p className="text-xs text-text-muted">
              支持 PNG, JPG, WebP, AVIF, GIF, SVG 等格式
            </p>
          </div>
          
          <input
            id="file-upload"
            type="file"
            multiple
            accept={SUPPORTED_INPUT_FORMATS.join(',')}
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>

        {/* Glow effect */}
        <div className="absolute inset-0 -z-10 rounded-2xl bg-primary/5 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    </motion.div>
  );
}